// Importing a bundle, from the pages LISTING — which, here, means CREATE.
//
// WHERE IMPORT IS INVOKED DECIDES WHAT IT DOES. This screen makes the file a
// complete, independent page or group of language variants: new pageIds, a new
// translation group, no relationship to whatever the file was exported from.
// Importing the same file twice gives two independent copies. The other verb —
// putting an edited file back into the pages it came from — lives on the
// page's own screen, ../page/import, and both are `BundleImport` below with a
// different mutation behind them: the flow, the plan table and the refusal
// sentences are one implementation because the server's rules are.
//
// TWO PRESSES, ALWAYS. Choosing a file runs the import with `dryRun` and draws
// what it would do; a second press applies it. That is not a courtesy — an
// import writes several rows through a store with no transaction (see
// model/page/bundle), so the moment to find out that a route collides is
// before the first write. For THIS verb that moment matters doubly: the
// commonest file — exported from this site, routes unedited — collides on
// every route, and the dry run is what puts that on screen as something to fix
// in the file rather than something to undo in the site.
//
// The plan is the SERVER's. Both presses are the same mutation with one flag
// different, so what is drawn here was produced by the code that does the work
// rather than by a second implementation of its rules — the same argument
// @common/schema/site/locale makes for the Translate screen's route preview.
//
// A plain `useMutation` rather than @repoflow/components/section's `Mutation`:
// the input is a FILE, and that component's whole job is driving a form-schema
// form over a value object. There is no schema here that a form could render.
import React, { useState } from "react";
// reactstrap's Button and NOT @repoflow/components/section's, which is the one
// every other screen here reaches for. That one reads a form's validity out of
// the section Mutation context to decide whether it may be pressed, and there
// is no form on this screen — it renders as a crash rather than as a button.
// What decides whether this one may be pressed is the PLAN.
import { Alert, Button, Container, Col, Row, Table } from "reactstrap";
import { useMutation } from "@apollo/client";
import { BreadcrumbItem } from "reactstrap";
import { CancellableContent } from "@repoflow/components/content-cancel";
import * as Notify from "@repoflow/form-schema/notify";
import * as PageQL from "queries/page";

import { parseBundle, readFileText } from "components/bundle";

export const Sidebar = () => null;

export const Indice = () => <BreadcrumbItem>Import</BreadcrumbItem>;

// The model's refusals, in a sentence each — BOTH verbs' refusals, because the
// two screens share this function the way the two model paths share
// `assertEnvelope`: a code only one verb can raise simply never matches on the
// other screen.
//
// Every one of these is a state a file can be in, and every one arrives as a
// bare code inside a GraphQL error string — `page.import.route.taken:/pricing`.
// Matched as a SUBSTRING because what wraps it is not ours (Apollo's prefix
// varies with the link chain) and the offending value follows a colon. Anything
// unrecognised falls through to the original text: an unknown failure somebody
// can quote is worth more than a friendly sentence that loses it. Written the
// same way, and for the same reasons, as `translateError` on the Translate
// screen.
export const importError = (error) => {
  const text = (error && error.toString()) || "";
  const detail = (text.split(/page\.import\.[a-z.]+:/)[1] || "").trim();

  if (text.includes("page.import.format")) {
    return `That file was written by a different version of this service (${detail || "unknown"}). Export the page again and re-apply your edits.`;
  }

  if (text.includes("page.import.site.mismatch")) {
    return `That file was exported from another site (${detail || "elsewhere"}). A bundle carries no sections, languages or files, so it only means anything in the site it came from.`;
  }

  if (text.includes("page.import.page.mismatch")) {
    return `That file is not this page's data (this page is ${detail || "another page"}). Importing here updates this page and its languages; to bring the file in as a new page, use Import on the pages listing.`;
  }

  if (text.includes("page.import.page.notfound")) {
    return `That file names a page this site does not have (${detail || "unknown"}). Importing here updates pages; Import on the pages listing is what creates them.`;
  }

  if (text.includes("page.import.page.duplicate")) {
    return `That file carries the same page twice (${detail || "one of them"}).`;
  }

  if (text.includes("page.import.group.mismatch")) {
    return `That file moves a page into a different translation group (${detail || "one of them"}). Only Translate does that.`;
  }

  if (text.includes("page.import.group.split")) {
    return `That file carries pages from more than one translation group (${detail || "one of them"}). Export and import one page at a time.`;
  }

  if (text.includes("page.import.source.unknown")) {
    return `That file names a structure source it does not carry (${detail || "unknown"}).`;
  }

  if (text.includes("page.import.route.taken")) {
    return `Another page of this site would be on ${detail || "that route"}. Two pages on one route is resolved by whichever was created first, so this is refused rather than applied — edit the file's routes and choose it again.`;
  }

  if (text.includes("page.import.locale.exists")) {
    return `Two pages of this group would be written in ${detail || "the same language"}.`;
  }

  if (text.includes("page.import.locale.undeclared")) {
    return `This site does not declare ${detail || "that language"}. Add it under Locales on the site's Translations tab first.`;
  }

  if (text.includes("page.import.invalid")) {
    return "That file is not a page bundle this service can read.";
  }

  return text;
};

// A row the plan says does something. A create's `changed` is empty — there is
// nothing stored to differ from — so the ACTION is what says the row is not a
// no-op; inferring from the list alone is what made the old guard unable to
// count a create as a change.
export const countsAsChange = (page) =>
  page.action === "create" || page.changed.length > 0;

// What the plan says about one page, in the order somebody reads it.
//
// "New page" for a create and "No change" for an untouched update, rather than
// an empty cell for either: an import of a group where one language was edited
// leaves every other row unchanged, and a blank there reads as a screen that
// failed to load rather than as the answer.
export const Plan = ({ pages }) => (
  <Table size="sm" className="mt-3">
    <thead>
      <tr>
        <th>Language</th>
        <th>Title</th>
        <th>Route</th>
        <th>Changes</th>
      </tr>
    </thead>
    <tbody>
      {pages.map((page) => (
        <tr key={page.pageId}>
          <td>{page.locale || <span className="text-muted">site default</span>}</td>
          <td>{page.title}</td>
          <td>{page.route || <span className="text-muted">none</span>}</td>
          <td>
            {page.action === "create" ? (
              "New page"
            ) : page.changed.length === 0 ? (
              <span className="text-muted">No change</span>
            ) : (
              page.changed.join(", ")
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);

// The choose-plan-apply flow, shared by both import screens.
//
// What differs between the verbs is the MUTATION, where the result sits in its
// answer, and where a finished apply lands — all of which the caller hands in.
// What must not differ is everything else: the two-press flow, the plan table,
// the guard and the sentences, because a person moving between the two screens
// is moving between two invocations of one feature.
export const BundleImport = ({ intro, mutation, variablesOf, resultOf, evict, onApplied }) => {
  // One piece of state and not four. The file, the plan it produced and the
  // reason it produced none are one fact about one file — held apart, a screen
  // can show the plan for a file that has since been replaced.
  const [state, setState] = useState({ filename: null, bundle: null, plan: null, error: null });

  const [run, { loading }] = useMutation(mutation);

  const call = async (bundle, dryRun) => {
    const { data } = await run({
      variables: variablesOf({ bundle, dryRun }),
      // ON THE APPLY ONLY, and passed per call rather than on the hook for
      // exactly that reason: a dry run wrote nothing, so evicting every page
      // cache entry would make the listing refetch to learn what it already
      // knew. On a real apply it is what makes the listing and every open page
      // screen re-read — see queries/page.update for the literal it greps for.
      ...(dryRun ? {} : { update: evict }),
    });

    return resultOf(data);
  };

  const onFile = async (event) => {
    const file = event.target.files && event.target.files[0];

    // The input is CLEARED after every read, so choosing the same file twice in
    // a row fires `change` the second time. Without it, fixing the file on disk
    // and re-choosing it does nothing and reads as a broken screen.
    event.target.value = "";

    if (!file) {
      return;
    }

    try {
      const bundle = parseBundle(await readFileText(file));
      const result = await call(bundle, true);

      setState({ filename: file.name, bundle, plan: result.pages, error: null });
    } catch (error) {
      setState({ filename: file.name, bundle: null, plan: null, error: importError(error) });
    }
  };

  const apply = async () => {
    try {
      await call(state.bundle, false);
      Notify.success("Imported.");
      onApplied();
    } catch (error) {
      // The plan is left on screen. The refusals are all about the FILE, so the
      // one thing somebody does next is read it again — and a screen that
      // cleared itself would have taken away what they are reading about.
      setState((current) => ({ ...current, error: importError(error) }));
    }
  };

  const changes = (state.plan || []).filter(countsAsChange).length;

  return (
    <CancellableContent>
      <Container className="mt-4">
        <Row>
          <Col>
            {intro}
            <input
              type="file"
              aria-label="Bundle file"
              accept="application/json,.json"
              onChange={onFile}
            />
          </Col>
        </Row>

        {state.error && (
          <Row className="mt-3">
            <Col>
              <Alert color="danger">{state.error}</Alert>
            </Col>
          </Row>
        )}

        {state.plan && (
          <Row>
            <Col>
              <h5 className="mt-4">{state.filename}</h5>
              {changes === 0 ? (
                <Alert color="info">
                  This file matches what is already stored. Importing it would
                  change nothing.
                </Alert>
              ) : null}
              <Plan pages={state.plan} />
              <Button color="primary" disabled={loading || changes === 0} onClick={apply}>
                Import
              </Button>
            </Col>
          </Row>
        )}
      </Container>
    </CancellableContent>
  );
};

export const Content = ({
  history,
  match: {
    params,
    params: { siteId },
  },
}) => (
  <BundleImport
    // Says plainly which verb this screen is, because the same file does two
    // different things depending on where it is dropped — and "adds no page"
    // was this paragraph's load-bearing clause until the verbs split.
    intro={
      <p>
        A bundle is a page and every other language of it, exported from a
        page&apos;s own screen. Importing it here <strong>creates new
        pages</strong>: a complete, independent copy of the file&apos;s group,
        with no relationship to whatever it was exported from. To update the
        pages a file came from, use Import on that page&apos;s screen. Routes
        must be free — edit them in the file first if the original pages still
        hold them.
      </p>
    }
    mutation={PageQL.importBundle}
    variablesOf={(input) => ({ siteId, input })}
    resultOf={(data) => data.viewer.account.sites.get.pages.import}
    evict={PageQL.update(params)}
    onApplied={() => history.replace(`/site/${siteId}/page`)}
  />
);
